<template>
  <!-- Hoja del campo (o de la clave): diálogo dentro de .ed, sobre el editor desenfocado. A la izquierda
       lo que se edita (una pregunta por formulario, regla, pregunta vacía y formulario sin la pregunta); a la
       derecha «Así se ejecutará»: el ejemplo y, debajo, solo lo que la izquierda no dice (conversión y avisos).
       No muta nada: emite y el editor aplica (con deshacer). -->
  <div class="fs-layer" @keydown="onKey">
    <div class="fs-back" aria-hidden="true" @click="onBack" />
    <section ref="cardRef" class="fs" :class="{ key: isKey }" role="dialog" aria-modal="true" :aria-labelledby="uid + '-t'">
      <header class="fs-head">
        <div class="fs-top">
          <div v-if="pos && pos.n > 1" class="fs-nav">
            <button ref="prevRef" type="button" class="fs-navb" :disabled="pos.i <= 1" :title="t('fs.prev')" :aria-label="t('fs.prev')"
                    @click="nav(-1)">‹</button>
            <span class="fs-pos">{{ pos.i }} / {{ pos.n }}</span>
            <button ref="nextRef" type="button" class="fs-navb" :disabled="pos.i >= pos.n" :title="t('fs.next')" :aria-label="t('fs.next')"
                    @click="nav(1)">›</button>
          </div>
          <span class="mc-ico entity" aria-hidden="true">E</span>
          <h2 :id="uid + '-t'" ref="titleRef" class="fs-title" tabindex="-1">
            <span class="fs-ent">{{ entityName }}</span><span class="fs-crumb" aria-hidden="true">›</span><span class="fs-fname">{{ field.name }}</span>
          </h2>
          <span class="fs-type"><b>{{ glyph }}</b>{{ typeName }}</span>
          <span v-if="field.column" class="fs-colname">{{ t('fs.column', { c: field.column }) }}</span>
          <span class="grow" />
          <span v-if="isKey" class="fs-keytag">{{ t('fs.keyTitle', { what: t('key.' + field.keyRole) }) }}</span>
          <button v-else-if="warnings.length" type="button" class="fs-wcount" :class="warnLevel" @click="toWarnings">
            <span aria-hidden="true">{{ warnLevel === 'danger' ? '■' : '▲' }}</span>
            {{ warnings.length === 1 ? t('fs.warnOne') : t('fs.warnCount', { n: warnings.length }) }}
          </button>
          <button type="button" class="fs-x" :aria-label="t('ed.close')" :title="t('ed.close')" @click="emit('close')">✕</button>
        </div>
        <p v-if="!isKey && recipe.line" class="fs-line">{{ recipe.line }}</p>
      </header>

      <!-- ── Campo de la clave (B.3) ──────────────────────────────────────────── -->
      <div v-if="isKey" ref="bodyRef" class="fs-body one">
        <div class="fs-right">
          <p v-for="s in keyIntro" :key="s.code" class="fs-s lead">{{ s.text }}</p>
          <h4 class="fs-glabel">{{ t('rc.pf.title') }}</h4>
          <ul class="fs-keyrows">
            <li v-for="r in recipe.keyRows || []" :key="r.questId" :class="r.level">
              <span class="fs-p">P{{ r.priority || 1 }}</span>
              <span class="fs-pfn" :title="r.questId">{{ r.name }}</span>
              <span class="fs-kt">{{ r.text }}</span>
              <button type="button" class="btn ghost fs-sm" @click="emit('edit-source', { questId: r.questId, field: field.keyRole === 'id' ? 'idAlias' : 'origin' })">
                {{ t('fs.editSource') }}
              </button>
            </li>
          </ul>
          <div class="fs-gb fs-keyrest">
            <template v-for="s in keyRest" :key="s.code">
              <p v-if="s.level === 'text'" class="fs-s">{{ s.text }}</p>
              <p v-else-if="s.level === 'info'" class="fs-s info"><i class="fs-i" aria-hidden="true">i</i><span>{{ s.text }}</span></p>
              <p v-else class="fs-warn" :class="s.level"><b aria-hidden="true">{{ s.level === 'danger' ? '■' : '▲' }}</b><span>{{ s.text }}</span></p>
            </template>
          </div>
        </div>
      </div>

      <!-- ── Campo normal (B.2) ───────────────────────────────────────────────── -->
      <div v-else ref="bodyRef" class="fs-body">
        <div ref="leftRef" class="fs-left">
          <section class="fs-sec" :aria-labelledby="uid + '-c'">
            <h3 :id="uid + '-c'" class="fs-h"><span class="fs-n">1</span>{{ t('fs.chain') }}</h3>
            <ul class="fs-slots">
              <li v-for="sl in slots" :key="sl.questId" class="fs-slot" :class="{ off: !sl.enabled || sl.missing }">
                <div class="fs-slot-h">
                  <span class="fbadge" :style="sl.enabled && !sl.missing ? { '--fc': sl.color } : null">{{ sl.code }}</span>
                  <span class="fs-slot-n" :title="sl.questId">{{ sl.name }}</span>
                  <span v-if="sl.missing || !sl.enabled" class="fs-slot-st">{{ t(sl.missing ? 'src.missing' : 'fs.formOff') }}</span>
                </div>
                <div v-if="sl.q" :ref="(el) => { slotEls[sl.questId] = el; }" class="fs-q" :class="{ sys: sl.q.sys }" tabindex="0"
                     aria-keyshortcuts="Delete" :aria-label="slotLabel(sl)" @keydown="onSlotKey($event, sl)">
                  <span class="fs-qmain">
                    <span class="fs-ql">{{ sl.q.sys ? sl.q.label : quote(sl.q) }}<span v-if="sl.q.sys" class="fs-tag">{{ t('cv.tagSys') }}</span></span>
                    <span v-if="sl.q.sys || (sl.q.label && sl.q.label !== sl.q.alias)" class="fs-qa">{{ sl.q.alias }}</span>
                    <span v-if="sl.q.shared.length" class="fs-qshared">{{ t('fs.shared', { forms: sl.q.shared.join(', ') }) }}</span>
                  </span>
                  <button type="button" class="fs-ib del" tabindex="-1" :title="removeTitle(sl)" :aria-label="removeTitle(sl)"
                          @click="remove(sl)">⌫</button>
                </div>
                <div v-else-if="picking === sl.questId" class="fs-pick">
                  <PickList ref="pickRef" :options="sl.options" :placeholder="t('pk.search')" :empty-text="t('pk.none')" autofocus
                            @pick="(a) => add(sl, a)" @close="closePick" />
                </div>
                <template v-else-if="!sl.missing">
                  <template v-if="sl.options.length">
                    <button :ref="(el) => { addEls[sl.questId] = el; }" type="button" class="fs-add" @click="openPick(sl.questId)">{{ t('fs.pick') }}</button>
                    <div v-if="suggestedOf(sl).length" class="fs-sugg">
                      <span class="fs-sugg-l"><span aria-hidden="true">✦</span> {{ t('fs.suggested') }}</span>
                      <button v-for="o in suggestedOf(sl)" :key="o.value" type="button" class="fs-sugg-b" :title="o.sub || o.value"
                              @click="add(sl, o.value)">«{{ o.label }}»</button>
                    </div>
                  </template>
                  <p v-else class="fs-hint">{{ t('fs.noOption') }}</p>
                </template>
              </li>
              <li v-for="c in strays" :key="c.alias" class="fs-slot">
                <div class="fs-slot-h"><span class="fs-slot-n">{{ t('fs.stray') }}</span></div>
                <div class="fs-q dead" :title="t('cv.deadTitle')">
                  <span class="fs-qmain"><span class="fs-ql">{{ quote(c) }}</span><span class="fs-qa">{{ c.alias }}</span></span>
                  <button type="button" class="fs-ib del" :title="t('fs.remove')" :aria-label="t('fs.remove')" @click="emit('remove', c.index)">⌫</button>
                </div>
              </li>
            </ul>
          </section>

          <fieldset class="fs-sec">
            <legend class="fs-h"><span class="fs-n">2</span>{{ t('ins.merge') }}</legend>
            <div class="fs-cards">
              <label v-for="m in modes" :key="m" class="fs-card" :class="{ on: mode === m }">
                <input type="radio" class="fs-radio" :name="uid + '-m'" :value="m" :checked="mode === m"
                       @change="emit('rule', { mergeMode: m })" />
                <span class="fs-ct"><i class="fs-dot" aria-hidden="true" />{{ t('merge.' + m) }}</span>
                <span class="fs-cd">{{ t('merge.' + m + '.d' + (m === 'concat' && recipe.list ? 'List' : '')) }}</span>
                <span class="fs-mini" :class="{ warn: minis[m].warn }">{{ minis[m].seq }} ⇒ <b>{{ minis[m].res }}</b></span>
              </label>
            </div>
          </fieldset>

          <fieldset class="fs-sec">
            <legend class="fs-h"><span class="fs-n">3</span>{{ t('fs.empty') }}</legend>
            <div class="fs-cards">
              <label v-for="o in MISSING" :key="o" class="fs-card" :class="{ on: onEmpty === o }">
                <input type="radio" class="fs-radio" :name="uid + '-e'" :value="o" :checked="onEmpty === o"
                       @change="emit('rule', { onEmpty: o })" />
                <span class="fs-ct"><i class="fs-dot" aria-hidden="true" />{{ t('missing.' + o) }}</span>
                <span class="fs-cd">{{ t(o === 'keep' ? 'fs.missKeep.d' : 'fs.missClear.d') }}</span>
              </label>
            </div>
          </fieldset>

          <fieldset class="fs-sec">
            <legend class="fs-h"><span class="fs-n">4</span>{{ t('fs.missing') }}</legend>
            <div class="fs-cards">
              <label v-for="o in MISSING" :key="o" class="fs-card" :class="{ on: onMissing === o }">
                <input type="radio" class="fs-radio" :name="uid + '-o'" :value="o" :checked="onMissing === o"
                       @change="emit('rule', { onMissing: o })" />
                <span class="fs-ct"><i class="fs-dot" aria-hidden="true" />{{ t('missing.' + o) }}</span>
                <span class="fs-cd">{{ t((o === 'keep' ? 'fs.absentKeep.d' : 'fs.absentClear.d') + (silent.length ? '' : 'Any'), { forms: silentNames }) }}</span>
              </label>
            </div>
            <p v-if="!silent.length" class="fs-hint">{{ t('fs.absentNone') }}</p>
          </fieldset>
        </div>

        <div ref="rightRef" class="fs-right" aria-live="polite">
          <h3 class="fs-h run">{{ t('fs.run') }}</h3>
          <RecipeExample v-if="recipe.example" :seed="recipe.example" :rule="ruleNow" :forms="recipe.perForm || []" />
          <p v-else-if="chain.length" class="fs-noex">{{ t('ex.none') }}</p>

          <div v-if="groups.length" class="fs-groups">
            <div v-for="g in groups" :key="g.code" class="fs-g">
              <div class="fs-gl">{{ t('fs.g.' + g.code) }}</div>
              <div class="fs-gb">
                <p v-for="s in g.main" :key="s.code + s.text" class="fs-s">{{ s.text }}</p>
                <ul v-if="g.code === 'C' && samples.length" class="fs-samples">
                  <li v-for="sm in samples" :key="sm.raw" :class="sm.kind" :title="sm.title">
                    <span class="fs-sraw">«{{ sm.raw }}»</span><b aria-hidden="true">{{ sm.mark }}</b><span v-if="sm.value">{{ sm.value }}</span>
                  </li>
                </ul>
                <template v-for="s in g.rest" :key="s.code + s.text">
                  <p v-if="s.level === 'info'" class="fs-s info"><i class="fs-i" aria-hidden="true">i</i><span>{{ s.text }}</span></p>
                  <p v-else class="fs-warn" :class="s.level"><b aria-hidden="true">{{ s.level === 'danger' ? '■' : '▲' }}</b><span>{{ s.text }}</span></p>
                </template>
              </div>
            </div>
          </div>

        </div>
      </div>

      <footer class="fs-foot">
        <p v-if="saved && !isKey" class="fs-past"><i class="fs-i" aria-hidden="true">i</i><span>{{ t('rc.note.past') }}</span></p>
        <div class="fs-actions">
          <div class="fs-tools" />
          <button v-if="!isKey && changed" type="button" class="btn" @click="emit('revert')">{{ t('fs.revert') }}</button>
          <span class="grow" />
          <button type="button" class="btn primary fs-done" @click="emit('close')">{{ t('fs.done') }}</button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import PickList from '@vem/components/PickList.vue';
import RecipeExample from '@vem/components/RecipeExample.vue';
import { t } from '@vem/i18n.js';
import { shortName } from '@vem/catalog.js';
import { CONV_SAMPLES, mergeModesFor, showValue, typeFamily } from '@vem/recipe.js';

let seq = 0;

const props = defineProps({
  /** {id, cls, name, type, column, table, isExt, rootName, keyRole: 'id'|'origin'|null}: el mismo `field` de buildRecipe. */
  field: { type: Object, required: true },
  /** buildRecipe(...) del campo. */
  recipe: { type: Object, required: true },
  /** Conexiones del campo en orden de preferencia: [{index (en dto.maps), alias, label, sys, dead, cov: {k, N, dots}|null}]. */
  chain: { type: Array, default: () => [] },
  /** Regla del campo {mergeMode, onEmpty, onMissing}; null = last/keep/keep. */
  rule: { type: Object, default: null },
  /** Una fila por formulario fuente: [{questId, name, code, color, enabled, missing, q: {index, alias, label, sys, shared: [letras]}|null,
   *  options: preguntas de ese formulario que se pueden elegir (grupo «Sugeridas» primero)}]. */
  slots: { type: Array, default: () => [] },
  /** Conexiones cuya pregunta no trae ningún formulario fuente: [{index, alias, label}]. */
  strays: { type: Array, default: () => [] },
  /** Posición del campo entre las filas visibles del lienzo (1-based). */
  pos: { type: Object, default: null },
  /** La definición ya existe (dto.id): muestra rc.note.past. */
  saved: { type: Boolean, default: false },
  /** Fila pulsada, en coordenadas de .ed: origen de la animación de entrada. */
  origin: { type: Object, default: null },
});
const emit = defineEmits(['close', 'nav', 'rule', 'add', 'remove', 'revert', 'edit-source']);

const MISSING = ['keep', 'clear'];
const GROUPS = ['A', 'C', 'R', 'M', 'X'];
const uid = `fs-${++seq}`;

const cardRef = ref(null);
const titleRef = ref(null);
const leftRef = ref(null);
const rightRef = ref(null);
const bodyRef = ref(null);
const prevRef = ref(null);
const nextRef = ref(null);
const pickRef = ref(null);
const slotEls = {};
const addEls = {};
/** Formulario cuyo desplegable de preguntas está abierto (questId) o null. */
const picking = ref(null);

const isKey = computed(() => !!props.field.keyRole);
const ruleNow = computed(() => props.rule || { mergeMode: 'last', onEmpty: 'keep', onMissing: 'keep' });
const mode = computed(() => ruleNow.value.mergeMode || 'last');
const onEmpty = computed(() => ruleNow.value.onEmpty || 'keep');
const onMissing = computed(() => ruleNow.value.onMissing || 'keep');
/** Formularios fuente sin ninguna pregunta del campo (la sección 4 se ve siempre: vale también para los que se añadan). */
const silent = computed(() => (props.recipe.perForm || []).filter((f) => f.state === 'silent'));
const silentNames = computed(() => silent.value.map((f) => f.name).join(', '));
const family = computed(() => typeFamily(props.field.type));
const modes = computed(() => mergeModesFor(family.value, (props.recipe.perForm || []).filter((f) => f.state === 'feeds').length, mode.value));
const typeName = computed(() => (family.value === 'unsupported' ? shortName(props.field.type) || '?' : t('type.' + family.value)));
const glyph = computed(() => t('glyph.' + (family.value === 'dateAny' ? 'date' : family.value)));
const entityName = computed(() => props.field.entityName || shortName(props.field.cls));
const sentences = computed(() => props.recipe.sentences || []);
const warnings = computed(() => props.recipe.warnings || []);
const warnLevel = computed(() => (warnings.value.some((w) => w.level === 'danger') ? 'danger' : 'warn'));

const quote = (c) => `«${c.label || c.alias}»`;

/**
 * Frases por grupo (A · C · R · M · X): primero las de texto, luego notas y avisos, en su orden. De «De dónde»,
 * «Regla» y «En blanco» solo las notas y avisos (el texto repite las secciones 1-4), salvo la escalera de prioridad.
 */
const TEXT_GROUPS = ['C', 'X'];
const groups = computed(() => GROUPS.map((code) => {
  const items = sentences.value.filter((s) => s.group === code);
  const main = items.filter((s) => s.level === 'text' && (TEXT_GROUPS.includes(code) || s.code === 'rc.rule.prio.fN'));
  return { code, main, rest: items.filter((s) => s.level !== 'text') };
}).filter((g) => g.main.length || g.rest.length));

const keySentences = computed(() => sentences.value.filter((s) => s.group === 'K'));
const keyIntro = computed(() => keySentences.value.slice(0, 1));
const keyRest = computed(() => keySentences.value.slice(1));

/** Ejemplos ✓/✗/! de la conversión del tipo del campo (el valor convertido, si cambia la grafía). */
const SAMPLE = { ok: ['✓', 'conv.ok'], bad: ['✗', 'conv.bad'], warn: ['!', 'conv.thousands'] };
const samples = computed(() => (CONV_SAMPLES[family.value] || []).map((s) => {
  const shown = s.ok ? showValue(family.value, s.value) : '';
  return { raw: s.raw, kind: s.mark, mark: SAMPLE[s.mark][0], title: t(SAMPLE[s.mark][1]), value: shown !== s.raw ? shown : '' };
}));

/** Mini-ejemplos de las tarjetas de regla: A (menos prioridad) · B (más) · C (menos). */
const minis = computed(() => {
  const feeds = (props.recipe.perForm || []).filter((f) => f.state === 'feeds');
  const ladder = [...new Set(feeds.map((f) => f.priority || 1))].sort((a, b) => a - b);
  const multi = ladder.length >= 2;
  const lo = 'P' + ladder[ladder.length - 1];
  const hi = 'P' + ladder[0];
  const seq = multi ? `${lo} A · ${hi} B · ${lo} C` : 'A · B · C';
  const isText = family.value === 'text';
  return {
    last: { seq, res: 'C' },
    first: { seq, res: 'A' },
    priority: multi ? { seq, res: 'B' } : { seq: `${t('rc.mini.asLast')} · ${seq}`, res: 'C', warn: feeds.length >= 2 },
    concat: isText ? { seq, res: props.recipe.list ? '|A|B|C|' : 'A ↵ B ↵ C' } : { seq: `${t('rc.mini.asLast')} · ${seq}`, res: 'C', warn: true },
  };
});

const suggestedOf = (sl) => sl.options.filter((o) => o.group === t('fs.suggested'));
const removeTitle = (sl) => (sl.q.shared.length ? t('fs.removeShared', { forms: sl.q.shared.join(', ') }) : t('fs.remove'));
const slotLabel = (sl) => `${sl.code} · ${sl.q.sys ? sl.q.label : quote(sl.q)} · ${sl.q.alias}`;

// «Deshacer cambios del campo» solo cuando sus preguntas o su regla difieren de cómo estaban al abrirlo.
const sig = () => JSON.stringify([props.chain.map((c) => c.alias), mode.value, onEmpty.value, onMissing.value]);
const initialSig = ref(sig());
const changed = computed(() => sig() !== initialSig.value);

// ── Acciones (emiten; el editor aplica) ─────────────────────────────────────
/** Foco en la fila de un formulario: su pregunta o, si no tiene, su botón de elegir. */
async function focusSlot(questId) {
  await nextTick();
  (slotEls[questId] || addEls[questId])?.focus();
}

function remove(sl) {
  emit('remove', sl.q.index);
  focusSlot(sl.questId);
}

function add(sl, alias) {
  picking.value = null;
  emit('add', alias);
  focusSlot(sl.questId);
}

function openPick(questId) {
  picking.value = questId;
  nextTick(() => [].concat(pickRef.value || [])[0]?.focus());
}

function closePick() {
  const q = picking.value;
  picking.value = null;
  if (q) focusSlot(q);
}

function nav(d) {
  if (!props.pos) return;
  const i = props.pos.i + d;
  if (i >= 1 && i <= props.pos.n) emit('nav', d);
}

function toWarnings() {
  rightRef.value?.querySelector('.fs-warn')?.scrollIntoView({ block: 'center' });
}

function onBack() {
  if (picking.value) closePick();
  else emit('close');
}

// ── Teclado ──────────────────────────────────────────────────────────────────
function onSlotKey(e, sl) {
  if (e.target !== e.currentTarget || e.key !== 'Delete') return;
  remove(sl);
  e.preventDefault();
}

/** Esc por capas: primero el desplegable (y el foco vuelve a su disparador); si no, la hoja. */
function onKey(e) {
  if (e.key === 'Escape') {
    e.stopPropagation();
    e.preventDefault();
    if (picking.value) closePick();
    else if (!(e.target.closest && e.target.closest('.fs-pick'))) emit('close');
    return;
  }
  if (e.altKey && (e.key === 'PageUp' || e.key === 'PageDown')) {
    e.preventDefault();
    nav(e.key === 'PageUp' ? -1 : 1);
  }
}

/** Foco al abrir (o al pasar de campo): el título; sin conexiones, el buscador del primer formulario que puede elegir. */
function focusStart() {
  const first = !isKey.value && !props.chain.length && props.slots.find((sl) => !sl.q && sl.options.length);
  if (first) {
    openPick(first.questId);
    return;
  }
  const a = document.activeElement;
  if (a && (a === prevRef.value || a === nextRef.value) && !a.disabled) return;
  titleRef.value?.focus({ preventScroll: true });
}

watch(() => props.field.id, async () => {
  initialSig.value = sig();
  picking.value = null;
  await nextTick();
  [bodyRef.value, leftRef.value, rightRef.value].forEach((el) => { if (el) el.scrollTop = 0; });
  focusStart();
});

onMounted(() => {
  const card = cardRef.value;
  if (props.origin && card) {
    card.style.transformOrigin = `${props.origin.x - card.offsetLeft}px ${props.origin.y - card.offsetTop}px`;
  }
  focusStart();
});
</script>

<style>
/* ── Hoja del campo: capa dentro de .ed (el padre desenfoca barra y rejilla con .sheet-open) ── */
.vem-root .fs-layer {
  position: absolute; inset: 0; z-index: 30; display: grid; place-items: center; padding: 16px;
  container: fs / inline-size;
}
.vem-root .fs-back { position: absolute; inset: 0; background: var(--scrim, rgba(244, 246, 247, .55)); }
.vem-root .fs {
  position: relative; display: grid; grid-template-rows: auto minmax(0, 1fr) auto; width: min(1080px, 100%); height: min(860px, 100%);
  background: var(--panel); border: 1px solid var(--line2); border-top: 3px solid var(--lime); border-radius: 18px;
  box-shadow: var(--sheet-shadow, 0 30px 80px -24px rgba(31, 35, 40, .38)); overflow: hidden;
}
.vem-root .fs-enter-active .fs, .vem-root .fs-leave-active .fs { transition: opacity .18s ease, transform .18s cubic-bezier(.2, .8, .2, 1); }
.vem-root .fs-enter-active .fs-back, .vem-root .fs-leave-active .fs-back { transition: opacity .18s ease; }
.vem-root .fs-enter-from .fs, .vem-root .fs-leave-to .fs { opacity: 0; transform: scale(.96); }
.vem-root .fs-enter-from .fs-back, .vem-root .fs-leave-to .fs-back { opacity: 0; }

/* cabecera */
.vem-root .fs-head { padding: 12px 16px 12px 18px; border-bottom: 1px solid var(--line2); background: linear-gradient(180deg, var(--lime-bg), transparent 140%); }
.vem-root .fs-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; min-width: 0; }
.vem-root .fs-nav { display: inline-flex; align-items: center; gap: 2px; padding: 2px; border: 1px solid var(--line2); border-radius: 100px; background: var(--panel); }
.vem-root .fs-navb {
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 50%;
  background: none; color: var(--tx2); font-size: 17px; line-height: 1; cursor: pointer;
}
.vem-root .fs-navb:hover:not(:disabled) { background: var(--hover); color: var(--tx); }
.vem-root .fs-navb:disabled { opacity: .3; cursor: default; }
.vem-root .fs-pos { font-family: var(--mono); font-size: 11px; color: var(--tx2); padding: 0 3px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.vem-root .fs-title {
  display: flex; align-items: baseline; gap: 7px; min-width: 0; margin: 0; font-size: 17px; font-weight: 600;
  letter-spacing: -.01em; border-radius: 6px; outline: none; overflow-wrap: anywhere;
}
.vem-root .fs-title:focus, .vem-root .fs-title:focus-visible { outline: none; box-shadow: none; }
.vem-root .fs-ent { color: var(--lime); }
.vem-root .fs-crumb { color: var(--tx3); font-weight: 400; }
.vem-root .fs-fname { font-family: var(--mono); font-size: 16px; }
.vem-root .fs-type {
  display: inline-flex; align-items: center; gap: 6px; padding: 2px 9px 2px 3px; border: 1px solid var(--line2);
  border-radius: 100px; background: var(--panel); font-size: 11.5px; color: var(--tx2); white-space: nowrap;
}
.vem-root .fs-type b { font-family: var(--mono); font-size: 10px; font-weight: 600; padding: 1px 6px; border-radius: 100px; background: var(--mute-bg); color: var(--tx); }
.vem-root .fs-colname { font-family: var(--mono); font-size: 11px; color: var(--tx3); white-space: nowrap; }
.vem-root .fs-keytag {
  font-family: var(--mono); font-size: 10px; font-weight: 600; letter-spacing: .12em; text-transform: uppercase;
  color: var(--lime); background: var(--lime-bg); border-radius: 6px; padding: 4px 8px; white-space: nowrap;
}
.vem-root .fs-wcount {
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 11px; border-radius: 100px; cursor: pointer; white-space: nowrap;
  font-size: 12px; font-weight: 600; color: var(--amber); background: var(--amber-bg, rgba(168, 100, 0, .08)); border: 1px solid var(--amber-bd);
}
.vem-root .fs-wcount.danger { color: var(--danger); background: var(--danger-bg, rgba(192, 57, 43, .07)); border-color: var(--danger-bd); }
.vem-root .fs-x {
  display: grid; place-items: center; width: 32px; height: 32px; padding: 0; border: 0; border-radius: 50%;
  background: none; color: var(--tx2); font-size: 14px; cursor: pointer;
}
.vem-root .fs-x:hover { background: var(--hover); color: var(--tx); }
.vem-root .fs-line { margin: 9px 0 0; padding: 1px 0 1px 10px; border-left: 2px solid var(--teal); font-size: 13px; color: var(--tx2); line-height: 1.45; }

/* cuerpo: dos columnas con su propio scroll */
.vem-root .fs-body { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr); min-height: 0; }
.vem-root .fs-body.one { grid-template-columns: minmax(0, 1fr); overflow-y: auto; }
.vem-root .fs-left { background: var(--panel2); overflow-y: auto; padding: 16px 16px 22px; border-right: 1px solid var(--line2); }
.vem-root .fs-right { overflow-y: auto; padding: 16px 20px 22px; min-width: 0; }
.vem-root .fs-body.one .fs-right { max-width: 860px; overflow: visible; }

.vem-root .fs-sec { margin: 0; padding: 0; border: 0; min-width: 0; }
.vem-root .fs-sec + .fs-sec { margin-top: 22px; }
.vem-root .fs-h {
  display: flex; align-items: center; gap: 8px; margin: 0 0 10px; padding: 0; font-family: var(--mono); font-size: 10.5px;
  font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx2); word-break: normal;
}
.vem-root .fs-h.run { color: var(--mint); }
.vem-root .fs-n {
  display: grid; place-items: center; flex: 0 0 19px; height: 19px; border-radius: 50%; letter-spacing: 0;
  background: var(--panel); border: 1px solid var(--mint-bd); color: var(--mint); font-size: 10px;
}
.vem-root .fs-glabel {
  margin: 0 0 8px; font-family: var(--mono); font-size: 9.5px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3);
}

/* una fila por formulario fuente */
.vem-root .fs-slots { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }
.vem-root .fs-slot-h { display: flex; align-items: center; gap: 7px; margin-bottom: 5px; font-size: 12px; color: var(--tx2); }
.vem-root .fs-slot-n { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600; }
.vem-root .fs-slot-st { margin-left: auto; flex: 0 0 auto; font-size: 11px; color: var(--tx3); }
.vem-root .fs-slot.off .fs-slot-n { color: var(--tx3); text-decoration: line-through; }
.vem-root .fs-slot.off .fbadge { background: none; color: var(--tx3); box-shadow: inset 0 0 0 1px var(--line2); }
.vem-root .fs-slot .fs-add, .vem-root .fs-slot .fs-pick { margin-top: 0; }
.vem-root .fs-q {
  display: flex; align-items: flex-start; gap: 10px; padding: 9px 8px 9px 10px; background: var(--panel); border: 1px solid var(--line2);
  border-radius: 11px; box-shadow: 0 1px 2px var(--line); outline: none; transition: border-color .15s ease, box-shadow .15s ease;
}
.vem-root .fs-q:hover { border-color: var(--port); }
.vem-root .fs-q:focus-visible { border-color: var(--mint); box-shadow: 0 0 0 3px var(--mint-bg); }
.vem-root .fs-q.sys { border-style: dashed; }
.vem-root .fs-q.dead { border-style: dashed; border-color: var(--amber-bd); background: var(--amber-bg, rgba(168, 100, 0, .08)); }
.vem-root .fs-qmain { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.vem-root .fs-ql { font-weight: 600; font-size: 13px; overflow-wrap: anywhere; }
.vem-root .fs-qa { font-family: var(--mono); font-size: 10.5px; color: var(--tx3); overflow-wrap: anywhere; }
.vem-root .fs-qshared { margin-top: 3px; font-size: 11.5px; color: var(--tx2); }
.vem-root .fs-tag { margin-left: 6px; padding: 1px 5px; border-radius: 4px; background: var(--mute-bg); color: var(--tx2); font-family: var(--mono); font-size: 9px; font-weight: 500; vertical-align: 1px; }
.vem-root .fs-ib {
  display: grid; place-items: center; width: 26px; height: 26px; padding: 0; border: 0; border-radius: 7px;
  background: none; color: var(--tx2); font-size: 13px; cursor: pointer;
}
.vem-root .fs-ib:hover:not(:disabled) { background: var(--hover); color: var(--tx); }
.vem-root .fs-ib.del:hover { background: var(--danger-bg, rgba(192, 57, 43, .07)); color: var(--danger); }
.vem-root .fs-ib:disabled { opacity: .3; cursor: default; }
.vem-root .fs-pick {
  list-style: none; margin-top: 8px; padding: 8px; background: var(--panel); border: 1px solid var(--mint-bd);
  border-radius: 11px; box-shadow: var(--lift, 0 8px 24px -12px rgba(31, 35, 40, .25));
}
.vem-root .fs-add {
  display: flex; align-items: center; width: 100%; margin-top: 8px; padding: 9px 12px; text-align: left; cursor: pointer;
  background: none; border: 1px dashed var(--mint-bd); border-radius: 11px; color: var(--mint); font-weight: 600;
  transition: background .15s ease, border-color .15s ease;
}
.vem-root .fs-add:hover { background: var(--mint-bg); border-style: solid; }
.vem-root .fs-sugg { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 8px; font-size: 11.5px; color: var(--tx3); }
.vem-root .fs-sugg-l { color: var(--mint); font-weight: 600; }
.vem-root .fs-sugg-b {
  max-width: 100%; padding: 3px 10px; border: 1px solid var(--mint-bd); border-radius: 100px; background: var(--panel);
  color: var(--mint); font-size: 11.5px; cursor: pointer; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block;
}
.vem-root .fs-sugg-b:hover { background: var(--mint-bg); }
.vem-root .fs-hint { margin: 8px 2px 0; font-size: 11.5px; color: var(--tx3); }
.vem-root .fs-empty { margin: 0; padding: 10px 12px; border: 1px dashed var(--line2); border-radius: 11px; background: var(--panel); color: var(--tx2); font-size: 12.5px; }

/* tarjetas de regla y de «sin valor»: radios nativos ocultos a la vista */
.vem-root .fs-cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.vem-root .fs-card {
  position: relative; display: flex; flex-direction: column; gap: 3px; padding: 10px 11px 9px; cursor: pointer;
  background: var(--panel); border: 1px solid var(--line2); border-radius: 11px;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}
.vem-root .fs-card:hover { border-color: var(--port); }
.vem-root .fs-card.on, .vem-root .fs-card:has(:checked) { border-color: var(--mint); background: var(--mint-bg); box-shadow: inset 0 0 0 1px var(--mint); }
.vem-root .fs-card:has(:focus-visible) { outline: 2px solid var(--mint); outline-offset: 2px; }
.vem-root .fs-radio { position: absolute; width: 1px; height: 1px; margin: 0; opacity: 0; pointer-events: none; clip-path: inset(50%); }
.vem-root .fs-ct { display: flex; align-items: center; gap: 7px; font-size: 13px; font-weight: 600; }
.vem-root .fs-dot { position: relative; flex: 0 0 14px; height: 14px; border-radius: 50%; border: 1.5px solid var(--port); background: var(--panel); transition: border-color .15s ease; }
.vem-root .fs-dot::after {
  content: ''; position: absolute; inset: 2.5px; border-radius: 50%; background: var(--mint); transform: scale(0); transition: transform .15s ease;
}
.vem-root .fs-card.on .fs-dot, .vem-root .fs-card:has(:checked) .fs-dot { border-color: var(--mint); }
.vem-root .fs-card.on .fs-dot::after, .vem-root .fs-card:has(:checked) .fs-dot::after { transform: scale(1); }
.vem-root .fs-cd { padding-left: 21px; font-size: 11.5px; color: var(--tx2); line-height: 1.35; }
.vem-root .fs-mini { padding-left: 21px; margin-top: 3px; font-family: var(--mono); font-size: 10.5px; color: var(--tx3); }
.vem-root .fs-mini b { color: var(--tx); }
.vem-root .fs-mini.warn, .vem-root .fs-mini.warn b { color: var(--amber); }

/* «Así se ejecutará» */
.vem-root .fs-groups { display: flex; flex-direction: column; }
.vem-root .fs-h.run + .rx { margin-top: 0; padding-top: 0; border-top: 0; }
.vem-root .fs-right > .fs-groups { margin-top: 18px; padding-top: 4px; border-top: 1px solid var(--line2); }
.vem-root .fs-h.run + .fs-groups { margin-top: 0; padding-top: 0; border-top: 0; }
.vem-root .fs-g { display: grid; grid-template-columns: 88px minmax(0, 1fr); gap: 12px; padding: 11px 0; border-top: 1px solid var(--line); }
.vem-root .fs-g:first-child { border-top: 0; padding-top: 2px; }
.vem-root .fs-gl { padding-top: 3px; font-family: var(--mono); font-size: 9.5px; font-weight: 600; letter-spacing: .14em; text-transform: uppercase; color: var(--tx3); }
.vem-root .fs-gb > * + * { margin-top: 7px; }
.vem-root .fs-s { margin: 0; font-size: 13px; line-height: 1.5; color: var(--tx); }
.vem-root .fs-s.lead { font-size: 13.5px; }
.vem-root .fs-s.info { display: flex; gap: 8px; font-size: 12px; color: var(--tx2); line-height: 1.45; }
.vem-root .fs-i {
  display: grid; place-items: center; flex: 0 0 15px; height: 15px; margin-top: 1px; border-radius: 50%; border: 1px solid var(--tx3);
  color: var(--tx3); font: 600 9px/1 var(--mono); font-style: normal;
}
.vem-root .fs-warn {
  display: flex; gap: 8px; margin: 0; padding: 7px 10px; border-left: 3px solid var(--amber); border-radius: 8px;
  background: var(--amber-bg, rgba(168, 100, 0, .08)); font-size: 12.5px; line-height: 1.45; color: var(--tx);
}
.vem-root .fs-warn b { flex: none; color: var(--amber); font-size: 11px; line-height: 18px; }
.vem-root .fs-warn.danger { border-color: var(--danger); background: var(--danger-bg, rgba(192, 57, 43, .07)); }
.vem-root .fs-warn.danger b { color: var(--danger); }
.vem-root .fs-samples { display: flex; flex-wrap: wrap; gap: 5px; margin: 0; padding: 0; list-style: none; }
.vem-root .fs-samples li {
  display: inline-flex; align-items: baseline; gap: 5px; padding: 2px 8px; border: 1px solid var(--line2); border-radius: 7px;
  background: var(--panel2); font-family: var(--mono); font-size: 11px; color: var(--tx2);
}
.vem-root .fs-samples li b { font-weight: 700; }
.vem-root .fs-samples li.ok b { color: var(--mint); }
.vem-root .fs-samples li.bad b { color: var(--danger); }
.vem-root .fs-samples li.warn { border-color: var(--amber-bd); }
.vem-root .fs-samples li.warn b { color: var(--amber); }
.vem-root .fs-samples .fs-sraw { color: var(--tx); }

/* filas de la clave */
.vem-root .fs-keyrows { list-style: none; margin: 0; padding: 0; border: 1px solid var(--line2); border-radius: 11px; overflow: hidden; background: var(--panel); }
.vem-root .fs-keyrows li:first-child { border-top: 0; }
.vem-root .fs-p { justify-self: start; padding: 1px 5px; border-radius: 5px; background: var(--mint-bg); color: var(--mint); font-family: var(--mono); font-size: 10px; font-weight: 600; }
.vem-root .fs-pfn { font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.vem-root .fs-noex { margin: 18px 0 0; padding: 10px 12px; border: 1px dashed var(--amber-bd); border-radius: 10px; color: var(--amber); font-size: 12.5px; }

/* hoja de la clave */
.vem-root .fs-body.one .fs-s.lead { margin-bottom: 16px; }
.vem-root .fs-keyrows { margin-bottom: 16px; }
.vem-root .fs-keyrows li {
  display: grid; grid-template-columns: 30px minmax(0, 1fr) minmax(0, 1.4fr) auto; gap: 10px; align-items: center;
  padding: 8px 10px; border-top: 1px solid var(--line); font-size: 12.5px;
}
.vem-root .fs-keyrows li.danger { background: var(--danger-bg, rgba(192, 57, 43, .07)); }
.vem-root .fs-keyrows li.danger .fs-kt { color: var(--danger); font-weight: 600; }
.vem-root .fs-kt { color: var(--tx2); overflow-wrap: anywhere; }
.vem-root .btn.fs-sm { min-height: 30px; padding: 4px 10px; font-size: 12px; }

/* pie */
.vem-root .fs-foot { display: flex; flex-direction: column; gap: 8px; padding: 10px 16px 12px 18px; border-top: 1px solid var(--line2); background: var(--panel); }
.vem-root .fs-past { display: flex; gap: 8px; margin: 0; font-size: 12px; color: var(--tx2); line-height: 1.4; }
.vem-root .fs-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.vem-root .fs-tools { display: flex; gap: 8px; }
.vem-root .fs-tools:empty { display: none; }
.vem-root .fs-done { min-width: 112px; }

/* anchos del editor (no de la ventana: embebido, el editor es más estrecho) */
@container fs (max-width: 900px) {
  .vem-root .fs-body { grid-template-columns: minmax(0, 1fr); overflow-y: auto; }
  .vem-root .fs-left, .vem-root .fs-right { overflow: visible; }
  .vem-root .fs-left { border-right: 0; border-bottom: 1px solid var(--line2); }
}
@container fs (max-width: 760px) {
  .vem-root .fs { position: absolute; inset: 0; width: auto; height: auto; border-radius: 0; border-width: 3px 0 0; }
  .vem-root .fs-cards { grid-template-columns: minmax(0, 1fr); }
  .vem-root .fs-g { grid-template-columns: minmax(0, 1fr); gap: 4px; }
  .vem-root .fs-keyrows li { grid-template-columns: 30px minmax(0, 1fr); }
  .vem-root .fs-keyrows li > :nth-child(n + 3) { grid-column: 2; justify-self: start; }
}
</style>
